import { useMemo } from 'react';
import { noop } from 'lodash-es';
import { ResourceRowData } from '@features/components/CaseOverview/components/AssignedResources/types';
import { ResourceTable } from '@features/components/CaseOverview/components/AssignedResources/ResourceTable';
import { OPO_CONTACT_COLUMNS } from '@features/components/CaseOverview/components/AssignedResources/config';
import { OpoContactWithPrimary } from '@/api/apis/case';
import { OpoContactOrderByOption, OpoContactTypeFilter, OpoPrimaryContactFilter } from '@/api/apis/opo';
import { Uuid } from '@/api/types';
import { useOpoContactListQuery } from '@/queries/opo';
import { mapContactRowData, searchOpoContacts, sortContacts } from './utils';
import { getRenderOpoContactTableCell } from './config';

export type OpoContactsTableProps = {
  opoId?: Uuid;
  assignedOpoContacts: OpoContactWithPrimary[];
  onContactAssign: (data: ResourceRowData) => void;
  searchTerm: string;
};

export const OpoContactsTable = ({
  searchTerm,
  opoId,
  assignedOpoContacts,
  onContactAssign,
}: OpoContactsTableProps) => {
  const { data, isLoading } = useOpoContactListQuery(
    {
      pageIndex: 0,
      itemsPerPage: 100,
      primaryFilter: OpoPrimaryContactFilter.All,
      contactType: OpoContactTypeFilter.All,
      orderBy: OpoContactOrderByOption.CreatedAt,
      asc: false,
      organizationIds: opoId ? [opoId] : [],
    },
    {
      enabled: !!opoId,
    },
  );

  const renderCell = useMemo(() => getRenderOpoContactTableCell(onContactAssign), [onContactAssign]);

  const items = useMemo(() => {
    const contacts = data?.items ?? [];
    const searchResults = searchOpoContacts(searchTerm, contacts);
    const list = searchResults.map((contact) => mapContactRowData(contact, assignedOpoContacts));

    return list.sort(sortContacts);
  }, [data?.items, assignedOpoContacts, searchTerm]);

  return (
    <ResourceTable
      columns={OPO_CONTACT_COLUMNS}
      onUserAssign={onContactAssign}
      list={items}
      hasNextPage={false}
      isFirstLoading={isLoading}
      loadNextPage={noop}
      renderCell={renderCell}
    />
  );
};
